/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useState } from "react"
import * as yup from "yup"
import moment from "moment"
import numeral from "numeral"
import {
  Appearance,
  ViewStyle,
  TextInput
} from "react-native"
import { ShrimpPrice } from "../../../database"
import { FormProps } from "../form"
import { Field, Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { Divider, Radio, View, Modal, useDisclose } from "native-base"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { Text } from "../../text/text"
import { useStores } from "../../../models"
import { BatchStoreModel } from "../../../models/app-store/farm-store/batch-store/batch-store"
import { FormControl } from "../../form-control/form-control"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import _ from "lodash"
import { Button } from "../../button/button"

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const ROW_INPUT: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  alignSelf: "center",
  paddingVertical: 6,
  paddingHorizontal: 16,
  borderRadius: 38
}

export interface FarmShrimpPriceFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    country_id: any
    farm_id?: number
    shrimp_price_id?: number,
    region_id?: string,
    date?: string,
    currency_id?: string,
    creator?: any,
    updated_at?: string,
    private?: boolean
  }
}

/**
 * Describe your component here
 */
export const FarmShrimpPriceForm = observer(function FarmShrimpPriceForm(props: FarmShrimpPriceFormProps) {
  const {
    initialValues,
    onSubmit,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    ...rest
  } = props

  const { isOpen, onClose, onOpen } = useDisclose()

  const { appStore: { farmStore: { activeBatchStore } } } = useStores()
  const { batch } = activeBatchStore || BatchStoreModel.create()

  const [ source, setSource ] = useState<"public"|"private">("public")
  const [ shrimpPrice, setShrimpPrice ] = useState<any>(null)
  // const [ layoutPrivate, setLayoutPrivate ] = useState<LayoutRectangle>(null)

  // useEffect(() => {
  //   ShrimpPrice.fetch({
  //     id__eq: initialValues.shrimp_price_id
  //   }).then((shrimpPrices) => {
  //     const shrimpPrice: ShrimpPrice = shrimpPrices[0]
  //     if (shrimpPrice) setShrimpPrice(shrimpPrice.toArray())
  //   })
  // }, [initialValues.shrimp_price_id])


  useEffect(() => {
    if (initialValues?.shrimp_price_id)
      ShrimpPrice.fetch({ id__eq: initialValues?.shrimp_price_id })
        .then((shrimpPrices) => {
          const shrimpPrice: ShrimpPrice = shrimpPrices[0]
          if (shrimpPrice) {
            setShrimpPrice(shrimpPrice.toArray())
            setSource(shrimpPrice.getAttribute('private') ? "private" : "public")
          }
        })
  }, [initialValues?.shrimp_price_id])

  const onChangeSource = useCallback((value: any) => {
    setSource(value)
  }, [])

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("farmShrimpPriceForm.farmId")),
    shrimp_price_id: yup.number().nullable().label(translate("farmShrimpPriceForm.shrimpPriceId")),
    source: yup.string().oneOf(['public', 'private']).nullable().label(translate('pondForm.type')),
    size_100: yup.number().when('source', {
      is: 'public',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable().required().moreThan(0)
    }).label(translate("shrimpPriceScreen.size100"))
  })

  const input = {
    size_100: React.useRef<TextInput>(),
    size_90: React.useRef<TextInput>(),
    size_80: React.useRef<TextInput>(),
    size_70: React.useRef<TextInput>(),
    size_60: React.useRef<TextInput>(),
    size_50: React.useRef<TextInput>(),
    size_40: React.useRef<TextInput>(),
    size_30: React.useRef<TextInput>(),
    size_20: React.useRef<TextInput>(),
    size_110: React.useRef<TextInput>(),
    size_120: React.useRef<TextInput>(),
    size_130: React.useRef<TextInput>(),
    size_140: React.useRef<TextInput>(),
    size_150: React.useRef<TextInput>()
  }

  const nextInput = {
    size_100: () => input.size_90.current && input.size_90.current.focus(),
    size_90: () => input.size_80.current && input.size_80.current.focus(),
    size_80: () => input.size_70.current && input.size_70.current.focus(),
    size_70: () => input.size_60.current && input.size_60.current.focus(),
    size_60: () => input.size_50.current && input.size_50.current.focus(),
    size_50: () => input.size_40.current && input.size_40.current.focus(),
    size_40: () => input.size_30.current && input.size_30.current.focus(),
    size_30: () => input.size_20.current && input.size_20.current.focus(),
    size_20: () => input.size_110.current && input.size_110.current.focus(),
    size_110: () => input.size_120.current && input.size_120.current.focus(),
    size_120: () => input.size_130.current && input.size_130.current.focus(),
    size_130: () => input.size_140.current && input.size_140.current.focus(),
    size_140: () => input.size_150.current && input.size_150.current.focus(),
  }

  const onConfirm = useCallback((values) => () => {
    onSubmit && onSubmit(values)
    onClose()
  }, [onClose, onSubmit])

  const onSubmitForm = useCallback((values) => {
    if(source === "public") onSubmit(values)
    else onOpen()
  }, [onOpen, onSubmit, source])

  const rowInput = (key1, key2) => (
    <View style={ROW_INPUT}>
      {key1 && <NumeralField
        name={key1}
        txPrefix="shrimpPriceScreen"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1}}
      /> || <View flex={1} paddingX="16px" />}
      {key2 && <NumeralField
        name={key2}
        txPrefix="shrimpPriceScreen"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1, isRequired: key2 === 'size_100'  }}
      /> || <View flex={1} paddingX="16px" />}
    </View>
  )

  const labelComponent = useCallback(() => {
    const labelText = source === "public" ? "farmShrimpPriceForm.shrimpPriceId" : "farmShrimpPriceForm.shrimpPriceIdPrivate"
    return (
      <View>
        <Text tx={labelText} preset="title4" colorScheme={colorScheme} />
        {source === 'private' && <Text tx="farmShrimpPriceForm.shrimpPriceSubtitle" preset="body3" colorScheme={colorScheme} marginTop="6px" />}
      </View>
    )
  }, [colorScheme, source])

  const refUser = shrimpPrice && shrimpPrice.creator ? shrimpPrice.creator.name : '-'
  const refDate = shrimpPrice && shrimpPrice.updated_at ? moment(shrimpPrice.updated_at).format('DD MMM YYYY') : '-'

  return (
    <Formik
      enableReinitialize
      initialValues={{
        source,
        date: moment().format('YYYY-MM-DD'),
        size_20: shrimpPrice ? shrimpPrice.size_20 : null,
        size_30: shrimpPrice ? shrimpPrice.size_30 : null,
        size_40: shrimpPrice ? shrimpPrice.size_40 : null,
        size_50: shrimpPrice ? shrimpPrice.size_50 : null,
        size_60: shrimpPrice ? shrimpPrice.size_60 : null,
        size_70: shrimpPrice ? shrimpPrice.size_70 : null,
        size_80: shrimpPrice ? shrimpPrice.size_80 : null,
        size_90: shrimpPrice ? shrimpPrice.size_90 : null,
        size_100: shrimpPrice ? shrimpPrice.size_100 : null,
        size_110: shrimpPrice ? shrimpPrice.size_110 : null,
        size_120: shrimpPrice ? shrimpPrice.size_120 : null,
        size_130: shrimpPrice ? shrimpPrice.size_130 : null,
        size_140: shrimpPrice ? shrimpPrice.size_140 : null,
        size_150: shrimpPrice ? shrimpPrice.size_150 : null,
        currency_id: shrimpPrice ? shrimpPrice.currency_id : 'IDR',
        id: shrimpPrice ? shrimpPrice.id : null,
        private: source === 'private' ? 1 : null,
        shrimp_price_id: shrimpPrice ? shrimpPrice.id : null,
        farm_id: initialValues.farm_id,
        region_id: initialValues.region_id,
        country_id: initialValues.country_id,
        species_id: 1,
        creator: shrimpPrice ? shrimpPrice.creator : null,
        created_at: shrimpPrice ? shrimpPrice.created_at : null,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmitForm}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue, setFieldTouched }) => (
        <>
          <Text tx="farmShrimpPriceForm.title" preset="title3" marginX="16px" marginY="8px" colorScheme={colorScheme} />
          <Text
            tx="farmShrimpPriceForm.subtitle"
            preset="body3"
            marginX="16px"
            marginY="8px"
            colorScheme={colorScheme}
            color={nativeBaseTheme.colors.gray[colorScheme === "light" ? "700" : "300"]}
          />
          <FormControl
            labelTx="farmShrimpPriceForm.source"
            colorScheme={colorScheme}
            labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
            style={FORM_CONTROL}
          >
            <Radio.Group
              colorScheme={colorScheme}
              name="source"
              accessibilityLabel={translate("farmShrimpPriceForm.source")}
              value={source}
              onChange={onChangeSource}
            >
              <Radio value="public" mr="32px" mb="8px">
                <Text tx="farmShrimpPriceForm.newest" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
              <Radio value="private">
                <Text tx="farmShrimpPriceForm.private" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
            </Radio.Group>
          </FormControl>
          <ModelField
            key={`${values.farm_id}-shrimp_price_id-${source}`}
            name="shrimp_price_id"
            txPrefix="farmShrimpPriceForm"
            colorScheme={colorScheme}
            formControlProps={{
              labelComponent: labelComponent(),
              // labelTx: source === "public" ? "farmShrimpPriceForm.shrimpPriceId" : "farmShrimpPriceForm.shrimpPriceIdPrivate",
              // descriptionTx: source === "private" ? "farmShrimpPriceForm.shrimpPriceSubtitle" : null,
              helperText: source === "public" || !initialValues.private
                ? batch?.start_batch ? translate('farmShrimpPriceForm.helperText', { date: moment(batch.start_batch).format('DD MMM YYYY') }) : undefined
                : translate('farmShrimpPriceForm.privateHelperText', { user: refUser, date: refDate }),
              helperTextProps: { color: nativeBaseTheme.colors.gray[colorScheme === "light" ? "700" : "300"] },
            }}
            modelPickerProps={{
              Model: ShrimpPrice,
              titleTx: "farmShrimpPriceForm.shrimpPriceIdPlaceholder",
              dataLabel: "size_100",
              placeholderTx: source === "private" ? "farmShrimpPriceForm.shrimpPriceIdPrivatePlaceholder" : "farmShrimpPriceForm.shrimpPriceIdPlaceholder",
              fetchData: (model, search) => (
                model.fetch({
                  search,
                  with: 'region,currency',
                  sort: "date,desc",
                  date__gte: batch?.start_batch ? batch.start_batch : undefined,
                  per_page: batch?.start_batch ? 1000 : 100,
                  private__ne_or_null: 1
                })
              ),
              pickerActionsheetProps: {
                colorScheme: colorScheme,
                renderItem({ item }) {
                  const currencySymbol = item.currency && item.currency.symbol || item.currency_id
                  const regionName = item.region && item.region.full_name || '-'

                  return <View paddingY="12px" paddingX="16px" backgroundColor={item.id === values.shrimp_price_id ? nativeBaseTheme.colors.gray[colorScheme === 'light' ? "100" : "900"] : undefined}>
                    <View flexDirection="row" justifyContent="space-between">
                      <Text text={moment(item.date).format('DD MMMM YYYY')} preset="title4" colorScheme={colorScheme} />
                      <Text text={`${currencySymbol || ''} ${numeral(item.size_100).format('0,0.[00]')}`} preset="title4" colorScheme={colorScheme} />
                    </View>
                    <Text text={regionName} preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? "700" : "300"]} />
                    <Divider backgroundColor={nativeBaseTheme.colors.gray[colorScheme === 'light' ? "100" : "900"]} />
                  </View>
                },
              },
              renderSelectedItemText: (item) => {
                if (!item) return null;
                const currencySymbol = item.currency && item.currency.symbol || item.currency_id

                return item && `${currencySymbol || ''} ${numeral(item.size_100).format("0,0.[00]")}, ${moment(item.date).format("DD MMM YYYY")}, ${item.region.full_name}`
              },
              clearable: true,
              isDisabled: !values.farm_id,
              onValueChange: async(value, index, item) => {
                const shrimpPrice = item && item.toArray() || null
                await setFieldValue('shrimp_price_id', value || null)
                await setFieldValue('size_100', shrimpPrice && shrimpPrice.size_100 || null)
                await setShrimpPrice(shrimpPrice)
                await setFieldTouched('shrimp_price_id')
              },
            }}
          />
          {source === 'private' && (
            <View marginBottom="50px">
              {rowInput('size_150', 'size_140')}
              {rowInput('size_130', 'size_120')}
              {rowInput('size_110', 'size_100')}
              {rowInput('size_90', 'size_80')}
              {rowInput('size_70', 'size_60')}
              {rowInput('size_50', 'size_40')}
              {rowInput('size_30', 'size_20')}
            </View>
          )}
          <Modal isOpen={isOpen} onClose={onClose}>
            <View
              alignSelf="stretch"
              marginY="80px"
              marginX="28px"
              paddingY="24px"
              paddingX="16px"
              rounded="16px"
              backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
            >
              <View alignItems="center" marginBottom="12px">
                <Text
                  text={translate("farmShrimpPriceForm.privateShrimpPriceSaveTitle")}
                  transformText={_.upperFirst}
                  preset="title1"
                  colorScheme={colorScheme}
                  marginY="8px" marginX="7px"
                  color={color.primaryDarker}
                />
                <Text
                  text={translate("farmShrimpPriceForm.privateShrimpPriceSaveSubtitle")}
                  transformText={_.upperFirst}
                  preset="body2"
                  colorScheme={colorScheme}
                  marginBottom="8px"
                  textAlign="center"
                />
              </View>
              <View flexDirection="row" justifyContent="center">
                <Button
                  testID="jala:id/delete-yes-button"
                  text={translate('Yes, I\'m Sure').toUpperCase()}
                  preset="outline"
                  textPreset="title4"
                  colorScheme={colorScheme}
                  style={BUTTON_DETAIL_ITEM}
                  onPress={onConfirm(values)}
                />
                <View width="12px" />
                <Button
                  testID="jala:id/delete-no-button"
                  text={_.upperCase(translate('No'))}
                  preset="primary"
                  textPreset="title4"
                  colorScheme={colorScheme}
                  style={BUTTON_DETAIL_ITEM}
                  onPress={onClose}
                />
              </View>
            </View>
          </Modal>
        </>
      )}
    </Formik>
  )
})

FarmShrimpPriceForm.displayName = 'FarmShrimpPriceForm'
